import { useMemo } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import type { StepVariant } from "@repo/types";
import { priceFormatter } from "~/utils/price-formatter";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";

import { useBasketContext } from "../basket/context";

import {
  getAmount,
  getCurrentVariantStep,
  getNextVariantStep,
  getSteps,
  getVariants
} from "./getters";
import { modalVariantsStore } from "./store";
import type { ModalVariantsStore, VariantStep } from "./store.types";

function useVariantAmount(): ModalVariantsStore["amount"] {
  return useStore(modalVariantsStore, getAmount);
}

function useVariants(): ModalVariantsStore["variants"] {
  return useStore(modalVariantsStore, useShallow(getVariants));
}

type UseVariantModalStepsArgs = {
  productId: string | undefined;
  showPricesInclVat: boolean;
};

function useVariantModalSteps({
  productId,
  showPricesInclVat
}: UseVariantModalStepsArgs) {
  const { locale, currency } = useCurrentTillLocalisation();
  const product = useBasketContext((state) =>
    state.products.find((p) => p.id === productId)
  );

  const {
    steps: storeSteps,
    currentStep: storeCurrentStep,
    nextStep: storeNextStep
  } = useStore(modalVariantsStore, useShallow(getSteps));

  return useMemo(() => {
    const steps: StepVariant[] = storeSteps.flatMap((variantStep) => {
      const variant = product?.options.find(
        (variant) => variant.id === variantStep.id
      );
      if (!variant) return [];

      const choices = [
        ...variant.choices.map((choice) => {
          // Use pre-calculated prices directly from product.variants

          const formattedDisplayPrice = priceFormatter({
            value: showPricesInclVat
              ? choice.unitPriceInclVat
              : choice.unitPriceExclVat,
            currency,
            locale
          });

          return {
            id: choice.id,
            unitPrice: choice.unitPriceExclVat,
            unitPriceWithVat: choice.unitPriceInclVat,
            displayPrice: formattedDisplayPrice,
            name: choice.name,
            max: choice.maximum ?? undefined,
            amount:
              variantStep.choices.find((c) => c.id === choice.id)?.amount ?? 0
          };
        })
      ];

      const totalChoiceAmount =
        choices.reduce((acc, c) => acc + c.amount, 0) ?? 0;

      return {
        ...variant,
        choices,
        isCompleted: Boolean(variantStep.completed),
        required: variant.isRequired,
        isNext: storeNextStep?.id === variant.id,
        isCurrent: storeCurrentStep?.id === variant.id,
        isValid: variant.minimum ? totalChoiceAmount >= variant.minimum : true
      };
    });

    const currentStep = steps.find((step) => step.id === storeCurrentStep?.id);
    const nextStep = steps.find((step) => step.id === storeNextStep?.id);
    const isSubmitDisabled = steps.some((step) => !step.isValid);

    return {
      steps,
      currentStep,
      nextStep,
      isSubmitDisabled
    };
  }, [
    storeSteps,
    product?.options,
    storeNextStep?.id,
    storeCurrentStep?.id,
    showPricesInclVat,
    currency,
    locale
  ]);
}

function useCurrentVariantStep(): VariantStep | null {
  return useStore(modalVariantsStore, useShallow(getCurrentVariantStep));
}

function useNextVariantStep(): VariantStep | null {
  return useStore(modalVariantsStore, useShallow(getNextVariantStep));
}

export {
  useCurrentVariantStep,
  useNextVariantStep,
  useVariantAmount,
  useVariantModalSteps,
  useVariants
};
